import { ItemListRowData } from "@/components/item-list/types";
import type { CalculateViewLocation, FlatScrollIntoViewLocation } from "react-virtuoso";

export type HighlightDirection = "up" | "down";

export const getNextHighlightedItemId = ({
  fallbackHighlightedItemId,
  direction,
  highlightedItemId,
  orderedItemIds,
}: {
  fallbackHighlightedItemId?: string;
  direction: HighlightDirection;
  highlightedItemId?: string;
  orderedItemIds: string[];
}) => {
  if (orderedItemIds.length === 0) {
    return undefined;
  }

  if (!highlightedItemId && !fallbackHighlightedItemId) {
    return orderedItemIds[0];
  }

  const currentIndex = orderedItemIds.indexOf(highlightedItemId ?? fallbackHighlightedItemId!);
  if (currentIndex === -1) {
    return orderedItemIds[0];
  }

  if (direction === "up") {
    return orderedItemIds[Math.max(currentIndex - 1, 0)];
  }

  return orderedItemIds[Math.min(currentIndex + 1, orderedItemIds.length - 1)];
};

export const blurInteractiveElement = (element: Element | null) => {
  if (!(element instanceof HTMLElement)) {
    return;
  }

  const tagName = element.tagName.toUpperCase();
  const role = element.getAttribute("role");
  const isInteractiveElement =
    tagName === "BUTTON" ||
    tagName === "A" ||
    tagName === "SUMMARY" ||
    role === "button" ||
    role === "link" ||
    role === "menuitem";

  if (isInteractiveElement) {
    element.blur();
  }
};

export const suppressNextInteractiveClick = (element: Element | null) => {
  if (!(element instanceof HTMLElement)) {
    return;
  }

  const tagName = element.tagName.toUpperCase();
  const role = element.getAttribute("role");
  const isInteractiveElement =
    tagName === "BUTTON" ||
    tagName === "A" ||
    tagName === "SUMMARY" ||
    role === "button" ||
    role === "link" ||
    role === "menuitem";

  if (!isInteractiveElement) {
    return;
  }

  const preventClick = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  };

  element.addEventListener("click", preventClick, { capture: true, once: true });
};

export const getRowIndexByItemId = ({
  orderedItemIds,
  rows,
}: {
  orderedItemIds: string[];
  rows: ItemListRowData[];
}) => {
  const orderedIds = new Set(orderedItemIds);
  const rowIndexByItemId = new Map<string, number>();

  rows.forEach((row, index) => {
    if (!orderedIds.has(row.key)) {
      return;
    }

    rowIndexByItemId.set(row.key, index);
  });

  return rowIndexByItemId;
};

export const getScrollIntoViewLocation = ({
  direction,
  rowIndex,
}: {
  direction: HighlightDirection;
  rowIndex: number;
}): FlatScrollIntoViewLocation => ({
  index: rowIndex,
  behavior: "auto",
  calculateViewLocation: params =>
    calculateKeyboardScrollLocation({ direction, rowIndex, ...params }),
});

const calculateKeyboardScrollLocation = ({
  direction,
  itemTop,
  itemBottom,
  rowIndex,
  viewportTop,
  viewportBottom,
  locationParams: { behavior },
}: Parameters<CalculateViewLocation>[0] & {
  direction: HighlightDirection;
  rowIndex: number;
}) => {
  const itemHeight = itemBottom - itemTop;

  if (direction === "down") {
    if (itemBottom > viewportBottom - itemHeight) {
      return {
        index: rowIndex,
        behavior,
        align: "end" as const,
      };
    }

    return null;
  }

  if (itemTop < viewportTop) {
    return {
      index: rowIndex,
      behavior,
      align: "start" as const,
    };
  }

  return null;
};
